import React from 'react';
import { Calculator, IndianRupee } from 'lucide-react';

function POTaxSummaryTab({ items, isSameState = true }) {
  // Calculate totals
  let subtotal = 0;
  let totalGst = 0;
  let totalDiscount = 0;
  
  for (let i = 0; i < items.length; i++) {
    subtotal += items[i].amount_before_tax || 0;
    totalGst += items[i].gst_amount || 0;
    const discAmt = (items[i].quantity || 0) * (items[i].purchase_rate || 0) * (items[i].discount_percentage || 0) / 100;
    totalDiscount += discAmt;
  }
  
  const cgst = isSameState ? totalGst / 2 : 0;
  const sgst = isSameState ? totalGst / 2 : 0;
  const igst = isSameState ? 0 : totalGst;
  const grandTotal = subtotal + totalGst;
  
  // Group GST by percentage
  const gstBreakdown = {};
  for (let i = 0; i < items.length; i++) {
    const rate = items[i].gst_percentage || 0;
    if (!gstBreakdown[rate]) {
      gstBreakdown[rate] = { taxable: 0, cgst: 0, sgst: 0, igst: 0 };
    }
    gstBreakdown[rate].taxable += items[i].amount_before_tax || 0;
    if (isSameState) {
      gstBreakdown[rate].cgst += (items[i].gst_amount || 0) / 2;
      gstBreakdown[rate].sgst += (items[i].gst_amount || 0) / 2;
    } else {
      gstBreakdown[rate].igst += items[i].gst_amount || 0;
    }
  }
  
  return (
    <div className="space-y-6" data-testid="po-tax-summary-tab">
      {/* GST Breakdown Table */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Calculator className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-gray-800">GST Breakdown by Rate</h3>
        </div>
        
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-blue-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-blue-800">GST Rate</th>
                <th className="px-4 py-3 text-right font-semibold text-blue-800">Taxable Amount</th>
                {isSameState ? (
                  <>
                    <th className="px-4 py-3 text-right font-semibold text-blue-800">CGST</th>
                    <th className="px-4 py-3 text-right font-semibold text-blue-800">SGST</th>
                  </>
                ) : (
                  <th className="px-4 py-3 text-right font-semibold text-blue-800">IGST</th>
                )}
                <th className="px-4 py-3 text-right font-semibold text-blue-800">Total Tax</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {Object.keys(gstBreakdown).length === 0 ? (
                <tr>
                  <td colSpan={isSameState ? 5 : 4} className="px-4 py-8 text-center text-gray-500">
                    No items added yet
                  </td>
                </tr>
              ) : (
                Object.entries(gstBreakdown).map(([rate, data]) => (
                  <tr key={rate} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{rate}%</td>
                    <td className="px-4 py-3 text-right">₹{data.taxable.toFixed(2)}</td>
                    {isSameState ? (
                      <>
                        <td className="px-4 py-3 text-right">₹{data.cgst.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right">₹{data.sgst.toFixed(2)}</td>
                      </>
                    ) : (
                      <td className="px-4 py-3 text-right">₹{data.igst.toFixed(2)}</td>
                    )}
                    <td className="px-4 py-3 text-right font-medium">
                      ₹{(data.cgst + data.sgst + data.igst).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Grand Summary */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border">
        <div className="flex items-center gap-2 mb-4">
          <IndianRupee className="w-5 h-5 text-green-600" />
          <h3 className="font-semibold text-gray-800">Order Summary</h3>
        </div>
        
        <div className="space-y-3">
          <div className="flex justify-between text-gray-600">
            <span>Subtotal (Before Tax)</span>
            <span className="font-medium">₹{subtotal.toFixed(2)}</span>
          </div>
          
          {totalDiscount > 0 && (
            <div className="flex justify-between text-orange-600">
              <span>Total Discount</span>
              <span className="font-medium">-₹{totalDiscount.toFixed(2)}</span>
            </div>
          )}
          
          {isSameState ? (
            <>
              <div className="flex justify-between text-gray-600">
                <span>CGST</span>
                <span className="font-medium">₹{cgst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>SGST</span>
                <span className="font-medium">₹{sgst.toFixed(2)}</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between text-gray-600">
              <span>IGST</span>
              <span className="font-medium">₹{igst.toFixed(2)}</span>
            </div>
          )}
          
          <div className="border-t-2 border-gray-300 pt-3 mt-3">
            <div className="flex justify-between text-xl">
              <span className="font-bold text-gray-800">Grand Total</span>
              <span className="font-bold text-green-600">₹{grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tax Info Note */}
      <div className="text-xs text-gray-500 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
        <p>💡 <strong>Note:</strong> GST is calculated as {isSameState ? 'CGST + SGST (Intra-state)' : 'IGST (Inter-state)'} based on supplier location.</p>
      </div>
    </div>
  );
}

export default POTaxSummaryTab;
